/**
 * PlayerChrome — custom controls bar at the bottom of <BrandedPlayer>.
 *
 * Layout per Figma (left → right):
 *   [play/pause] [mute] [progress track …] [time] [fullscreen] [sponsor LR]
 *
 * Progress track has a green fill (--allstar-accent) over a translucent
 * white track, with a small white thumb. Click anywhere on the track to seek.
 *
 * The lower-right sponsor wordmark lives inside this bar (not as a separate
 * absolute overlay) because the Figma puts it in the chrome strip, sitting
 * right after the fullscreen button.
 */
import { MouseEvent, PointerEvent as ReactPointerEvent, useRef } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";

import type { ResolvedSponsorConfig } from "../../@types";
import { useRecoilValue } from "recoil";

import { BrandedViewerGeoState } from "../../State";
import { SponsorLogo } from "../SponsorAssets";
import { ALLSTAR_FALLBACK_LOWER_RIGHT } from "../../sponsors";
import { fireAdClickBeacon, useImpressionBeacon } from "../../tracking";
import {
  FullscreenChromeIcon,
  MutedIcon,
  PauseChromeIcon,
  PlayChromeIcon,
  VolumeIcon,
} from "./chromeIcons";

interface PlayerChromeProps {
  playing: boolean;
  muted: boolean;
  /** Element volume, 0..1, for the vertical volume slider. */
  volume: number;
  currentSec: number;
  durationSec: number;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  /** Set volume from a 0..1 fraction (0 mutes, >0 unmutes). */
  onSetVolume: (fraction: number) => void;
  onSeek: (fraction: number) => void;
  onToggleFullscreen: () => void;
  sponsor: ResolvedSponsorConfig | null;
  /** Bottom-playlist variant: shrink the LR sponsor wordmark so the
      always-on upper-left Allstar watermark feels relatively bigger
      and reads as the primary brand signature in this layout. */
  bottomPlaylist?: boolean;
}

const ChromeBar = styled(Flex)`
  align-items: center;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.55) 60%,
    rgba(0, 0, 0, 0.7) 100%
  );
  bottom: 0;
  gap: 8px;
  left: 0;
  padding: 22px 10px 8px;
  position: absolute;
  right: 0;
  z-index: 3;
`;

const IconBtn = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.92);
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 22px;
  justify-content: center;
  padding: 0;
  transition:
    color 120ms ease,
    transform 120ms ease;
  width: 22px;

  &:hover {
    color: #fff;
    transform: scale(1.06);
  }

  &:focus-visible {
    outline: 2px solid var(--allstar-accent, #3c87d2);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

/* Volume control: the mute IconBtn plus a vertical slider that reveals on
   hover/focus directly above it (YouTube-style), so it costs no horizontal
   room in the compact chrome row. */
const VolumeControl = styled(Box)`
  display: inline-flex;
  flex: 0 0 auto;
  position: relative;
`;

const VolumePopup = styled(Box)`
  background: rgba(0, 0, 0, 0.82);
  border-radius: 8px;
  bottom: calc(100% + 6px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  left: 50%;
  opacity: 0;
  padding: 10px 8px;
  position: absolute;
  transform: translateX(-50%);
  transition:
    opacity 120ms ease,
    visibility 120ms ease;
  visibility: hidden;
  z-index: 5;

  ${VolumeControl}:hover &,
  ${VolumeControl}:focus-within & {
    opacity: 1;
    visibility: visible;
  }
`;

const VolumeTrack = styled(Box)`
  background: rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  cursor: pointer;
  height: 70px;
  position: relative;
  touch-action: none;
  width: 4px;
`;

const VolumeFill = styled(Box)<{ $pct: number }>`
  background: var(--allstar-accent, #3c87d2);
  border-radius: 999px;
  bottom: 0;
  height: ${({ $pct }) => $pct}%;
  left: 0;
  position: absolute;
  width: 100%;
`;

const VolumeThumb = styled(Box)<{ $pct: number }>`
  background: #fff;
  border-radius: 50%;
  bottom: ${({ $pct }) => $pct}%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  height: 10px;
  left: 50%;
  position: absolute;
  transform: translate(-50%, 50%);
  width: 10px;
`;

const ProgressTrack = styled(Box)`
  background: rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  cursor: pointer;
  flex: 1 1 auto;
  height: 3px;
  position: relative;
  transition: height 120ms ease;

  &:hover {
    height: 5px;
  }
`;

const ProgressFill = styled(Box)<{ $pct: number }>`
  background: var(--allstar-accent, #3c87d2);
  border-radius: 999px;
  height: 100%;
  width: ${({ $pct }) => $pct}%;
`;

const ProgressThumb = styled(Box)<{ $pct: number }>`
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  height: 10px;
  left: ${({ $pct }) => $pct}%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
`;

/* Tight cluster for the right-of-progress controls. Sits inside
   ChromeBar but with its own smaller gap so the time label, fullscreen
   icon, and sponsor wordmark read as a single anchored unit on the
   right edge, instead of inheriting the chrome's 8px global spacing
   (which left them feeling adrift). */
const RightRail = styled(Flex)`
  align-items: center;
  flex: 0 0 auto;
  gap: 4px;
`;

/* Current time / total time readout (e.g. "0:15 - 0:27"). Tabular
   figures so the digits don't jitter as time advances. */
const TimeLabel = styled(Text)`
  color: rgba(255, 255, 255, 0.92);
  flex: 0 0 auto;
  font-feature-settings: "tnum" 1;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
`;

const SponsorChromeSlot = styled.a<{ $bp?: boolean }>`
  align-items: center;
  display: inline-flex;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.6));
  flex: 0 0 auto;
  text-decoration: none;
  transition: transform 120ms ease;

  /* Cap by height (not width) so any client-uploaded asset — wide wordmark,
     tall mark, or square icon — fits the chrome row's ~22px vertical budget.
     Width auto-scales to preserve aspect ratio; max-width prevents a very
     wide wordmark from pushing the fullscreen button off the row.

     Bottom-playlist variant shrinks the LR mark to 12px tall (max 50px
     wide). The publisher feedback for the bottom layout was that the
     LR sponsor felt heavier than the UL Allstar watermark — pulling it
     in tighter rebalances the attention to the upper-left mark, which
     is the embed's primary brand signature in that mode. */
  & > img,
  & > svg {
    display: block;
    height: ${({ $bp }) => ($bp ? "12px" : "16px")};
    max-width: ${({ $bp }) => ($bp ? "50px" : "70px")};
    object-fit: contain;
    width: auto;
  }

  &:hover {
    transform: scale(1.04);
  }
`;

const formatClock = (sec: number): string => {
  const safe = Math.max(0, Math.floor(sec));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const PlayerChrome = ({
  playing,
  muted,
  volume,
  currentSec,
  durationSec,
  onTogglePlay,
  onToggleMute,
  onSetVolume,
  onSeek,
  onToggleFullscreen,
  sponsor,
  bottomPlaylist,
}: PlayerChromeProps) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const volumeTrackRef = useRef<HTMLDivElement | null>(null);
  const volumeDraggingRef = useRef(false);

  const pct =
    durationSec > 0 ? Math.min(100, (currentSec / durationSec) * 100) : 0;

  // Effective level shown on the slider: muting drops the fill to 0 without
  // losing the underlying volume (unmuting restores it).
  const volumePct = Math.round((muted ? 0 : volume) * 100);

  const handleTrackClick = (e: MouseEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const fraction = (e.clientX - rect.left) / rect.width;
    onSeek(fraction);
  };

  // Vertical slider: top of the track = full volume, bottom = muted.
  const setVolumeFromPointer = (clientY: number) => {
    const el = volumeTrackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    onSetVolume(1 - (clientY - rect.top) / rect.height);
  };

  const handleVolumePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    volumeDraggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    setVolumeFromPointer(e.clientY);
  };

  const handleVolumePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!volumeDraggingRef.current) return;
    setVolumeFromPointer(e.clientY);
  };

  const handleVolumePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    volumeDraggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  /* Lower-right logo is locked to the Allstar mark across every
     variant (side-rail, bottom-playlist, mobile). We previously locked
     this slot to a fixed Allstar mark regardless of which campaign was
     active, but that meant LR impressions/clicks were attributed to the
     served paid campaign for a creative it never actually showed —
     poisoning every paid campaign's LR tally. Honor the served sponsor's
     own LR creative instead (falling back to the Allstar mark only when
     no campaign is live for this geo, exactly like the upper-left
     watermark). Attribution to `sponsor.campaignId` is now correct
     because the sponsor's own creative is what renders here. */
  const lr = sponsor?.slots.lowerRightLogo ?? ALLSTAR_FALLBACK_LOWER_RIGHT;

  const viewerGeo = useRecoilValue(BrandedViewerGeoState);
  const lowerRightImpressionRef = useImpressionBeacon({
    slot: "lowerRightLogo",
    creativeId: lr.id,
  });
  const handleLowerRightClick = () => {
    fireAdClickBeacon({
      campaignId: sponsor?.campaignId,
      slot: "lowerRightLogo",
      creativeId: lr.id,
      viewerGeo,
    });
  };

  return (
    <ChromeBar>
      <IconBtn
        type="button"
        aria-label={playing ? "Pause" : "Play"}
        onClick={onTogglePlay}
      >
        {playing ? <PauseChromeIcon size={12} /> : <PlayChromeIcon size={12} />}
      </IconBtn>
      <VolumeControl>
        <IconBtn
          type="button"
          aria-label={muted ? "Unmute" : "Mute"}
          onClick={onToggleMute}
        >
          {muted || volume === 0 ? (
            <MutedIcon size={12} />
          ) : (
            <VolumeIcon size={12} />
          )}
        </IconBtn>
        <VolumePopup>
          <VolumeTrack
            ref={volumeTrackRef}
            role="slider"
            aria-label="Volume"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={volumePct}
            onPointerDown={handleVolumePointerDown}
            onPointerMove={handleVolumePointerMove}
            onPointerUp={handleVolumePointerUp}
          >
            <VolumeFill $pct={volumePct} />
            <VolumeThumb $pct={volumePct} />
          </VolumeTrack>
        </VolumePopup>
      </VolumeControl>
      <ProgressTrack ref={trackRef} onClick={handleTrackClick}>
        <ProgressFill $pct={pct} />
        <ProgressThumb $pct={pct} />
      </ProgressTrack>
      <RightRail>
        <TimeLabel>
          {formatClock(currentSec)} - {formatClock(durationSec)}
        </TimeLabel>
        <IconBtn
          type="button"
          aria-label="Fullscreen"
          onClick={onToggleFullscreen}
        >
          <FullscreenChromeIcon size={12} />
        </IconBtn>
        <SponsorChromeSlot
          href={lr.ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={lr.altText}
          ref={lowerRightImpressionRef}
          onClick={handleLowerRightClick}
          $bp={bottomPlaylist}
        >
          {/* Size is controlled by SponsorChromeSlot's CSS (height 22px,
              width auto). Passing width here would conflict with the
              aspect-preserving rule for non-wordmark assets. */}
          <SponsorLogo creative={lr} variant="wordmark" />
        </SponsorChromeSlot>
      </RightRail>
    </ChromeBar>
  );
};
